import { useMemo } from "react";
import { useCrmCampanhas, useCrmConversas } from "@/hooks/useCrm";
import {
  ESTAGIOS_FECHADOS,
  ORIGEM_LABEL,
  type CrmConversa,
  type CrmEstagio,
  type CrmOrigem,
} from "@/types/crm";
import { FILA_ESTAGIOS } from "@/components/crm/crmUtils";

const DAY_MS = 86_400_000;

export type DonutGrupo = {
  key: string;
  label: string;
  value: number;
  color: string;
};

function startOfWeek(d: Date) {
  const copy = new Date(d);
  const day = copy.getDay();
  const diff = day === 0 ? 6 : day - 1;
  copy.setHours(0, 0, 0, 0);
  copy.setDate(copy.getDate() - diff);
  return copy;
}

function weekLabel(d: Date) {
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

function countByEstagio(conversas: CrmConversa[], estagio: CrmEstagio) {
  return conversas.filter((c) => c.estagio === estagio).length;
}

function createdInWindow(conversas: CrmConversa[], from: number, to: number, estagio?: CrmEstagio) {
  return conversas.filter((c) => {
    const t = new Date(c.created_at).getTime();
    if (t < from || t >= to) return false;
    return estagio ? c.estagio === estagio : true;
  }).length;
}

export function useCrmDashboard() {
  const conversasQuery = useCrmConversas();
  const campanhasQuery = useCrmCampanhas();
  const conversas = conversasQuery.data ?? [];

  const stats = useMemo(() => {
    const now = Date.now();
    const d7 = now - 7 * DAY_MS;
    const d14 = now - 14 * DAY_MS;

    const abertas = conversas.filter((c) => !ESTAGIOS_FECHADOS.includes(c.estagio));
    const naoLidas = conversas.reduce((acc, c) => acc + (c.nao_lidas || 0), 0);
    const novos = countByEstagio(conversas, "novo");
    const humano = countByEstagio(conversas, "humano_atendendo");
    const ganho = countByEstagio(conversas, "fechado_ganho");
    const perdido = countByEstagio(conversas, "fechado_perdido");
    const semInteresse = countByEstagio(conversas, "sem_interesse");
    const encerradas = ganho + perdido + semInteresse;
    const conversao = encerradas > 0 ? Math.round((ganho / encerradas) * 100) : 0;

    const kpiDelta = (estagio: CrmEstagio) => {
      const atual = createdInWindow(conversas, d7, now, estagio);
      const anterior = createdInWindow(conversas, d14, d7, estagio);
      if (anterior === 0) return { percent: null as number | null, recentes: atual };
      return { percent: Math.round(((atual - anterior) / anterior) * 100), recentes: atual };
    };

    const semanaAtual = startOfWeek(new Date());
    const semanas = Array.from({ length: 8 }, (_, i) => {
      const start = new Date(semanaAtual);
      start.setDate(start.getDate() - (7 - i) * 7);
      const end = new Date(start);
      end.setDate(end.getDate() + 7);
      const from = start.getTime();
      const to = end.getTime();
      const inbound: CrmOrigem[] = ["inbound_bot", "inbound_humano", "handoff_bot"];
      const campanha: CrmOrigem[] = ["campanha_marketing", "campanha_renovacao"];
      return {
        semana: weekLabel(start),
        inbound: conversas.filter((c) => {
          const t = new Date(c.created_at).getTime();
          return t >= from && t < to && inbound.includes(c.origem);
        }).length,
        campanha: conversas.filter((c) => {
          const t = new Date(c.created_at).getTime();
          return t >= from && t < to && campanha.includes(c.origem);
        }).length,
      };
    });

    const donut: DonutGrupo[] = [
      {
        key: "aberto",
        label: "Abertas",
        value: conversas.filter((c) =>
          ["novo", "aguardando_resposta", "renovacao_andamento"].includes(c.estagio)
        ).length,
        color: "#252D30",
      },
      {
        key: "ia",
        label: "IA",
        value: countByEstagio(conversas, "ia_atendendo"),
        color: "#4B5563",
      },
      {
        key: "humano",
        label: "Humano",
        value: humano,
        color: "#6B7280",
      },
      {
        key: "fechado",
        label: "Fechadas",
        value: encerradas,
        color: "#9CA3AF",
      },
    ].filter((g) => g.value > 0);

    const donutTotal = donut.reduce((acc, g) => acc + g.value, 0);

    const porOrigem = (Object.keys(ORIGEM_LABEL) as CrmOrigem[])
      .map((origem) => ({
        origem,
        label: ORIGEM_LABEL[origem],
        value: conversas.filter((c) => c.origem === origem).length,
      }))
      .filter((o) => o.value > 0);

    const recentes = [...conversas]
      .sort((a, b) => new Date(b.ultima_msg_em).getTime() - new Date(a.ultima_msg_em).getTime())
      .slice(0, 8);

    const proximaFila = conversas.find((c) => FILA_ESTAGIOS.includes(c.estagio)) ?? null;

    return {
      total: conversas.length,
      abertas: abertas.length,
      naoLidas,
      novos,
      humano,
      ganho,
      conversao,
      encerradas,
      kpiNovos: kpiDelta("novo"),
      kpiHumano: kpiDelta("humano_atendendo"),
      kpiGanho: kpiDelta("fechado_ganho"),
      semanas,
      donut,
      donutTotal,
      porOrigem,
      recentes,
      proximaFila,
    };
  }, [conversas]);

  return {
    ...stats,
    conversas,
    campanhas: campanhasQuery.data ?? [],
    isLoading: conversasQuery.isLoading,
  };
}
